import {
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ConnectionMode,
  type Connection,
  type Edge,
  MarkerType
} from '@xyflow/react'
import React, {useEffect, useRef} from 'react'

import '@xyflow/react/dist/style.css'

import {FlowProvider} from '../FlowContext'
import {mapFlowToNodes, mapFlowToEdges, getCanvasHeight} from '../helpers'
import {useNodeDragConstraint} from '../hooks/useNodeDragConstraint'
import {
  type FlowEntity,
  type FlowSetup,
  type FlowLink,
  type FlowValidator,
  type TerminalNode
} from '../types'

import {FloatingEdge} from './FloatingEdge'
import {FlowNode} from './FlowNode'

const NODE_TYPE = 'flowNode'
const nodeTypes = {[NODE_TYPE]: FlowNode}
const edgeTypes = {floating: FloatingEdge}

const defaultEdgeOptions = {
  type: 'floating',
  style: {stroke: 'var(--primary)', strokeWidth: 1.5},
  markerEnd: {type: MarkerType.ArrowClosed, color: 'var(--primary)'}
}

const connectionLineStyle = {stroke: 'var(--primary_selected)', strokeWidth: 2}

interface FlowCanvasProps {
  setup: FlowSetup
  entities: FlowEntity[]
  terminalNode: TerminalNode
  validator: FlowValidator
  onLinkAdded: (link: FlowLink) => void
  onLinkRemoved: (link: FlowLink) => void
  onNodeMoved: (nodeId: string, x: number, y: number) => void
  onConnectionRejected?: (reason: 'duplicate' | 'cycle' | 'maxDepth') => void
}

export const FlowCanvas: React.FC<FlowCanvasProps> = (props) => {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner {...props} />
    </ReactFlowProvider>
  )
}

const FlowCanvasInner: React.FC<FlowCanvasProps> = ({
  setup,
  entities,
  terminalNode,
  validator,
  onLinkAdded,
  onLinkRemoved,
  onNodeMoved,
  onConnectionRejected
}) => {
  const {fitView} = useReactFlow()

  const [nodes, setNodes, onNodesChange] = useNodesState(
    mapFlowToNodes(setup, entities, terminalNode, NODE_TYPE)
  )

  const [edges, setEdges, onEdgesChange] = useEdgesState(mapFlowToEdges(setup))

  const containerRef = useRef<HTMLDivElement>(null)
  const {onNodeDragStart, onNodeDragStop} = useNodeDragConstraint(containerRef, onNodeMoved)

  useEffect(() => {
    setNodes(mapFlowToNodes(setup, entities, terminalNode, NODE_TYPE))
    setEdges(mapFlowToEdges(setup))
    void fitView({padding: 0.1})
  }, [setup, entities, terminalNode, setNodes, setEdges, fitView])

  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver(() => void fitView({padding: 0.1}))
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [fitView])

  const onConnect = (connection: Connection) => {
    const result = validator.isConnectionValid(connection.source, connection.target, setup.links)
    if (!result.valid) {
      onConnectionRejected?.(result.reason)
      return
    }

    setEdges((edges) => addEdge(connection, edges))
    onLinkAdded({sourceNodeId: connection.source, targetNodeId: connection.target})
  }

  const onEdgeClick = (_event: React.MouseEvent, edge: Edge) => {
    setEdges((edges) => edges.filter((e) => e.id !== edge.id))
    onLinkRemoved({sourceNodeId: edge.source, targetNodeId: edge.target})
  }

  const canvasHeight = getCanvasHeight(entities.length)

  return (
    <FlowProvider terminalNodeId={terminalNode.id}>
      <div
        ref={containerRef}
        style={{height: canvasHeight}}
        className="bg-white border-2 border-dashed border-border_highlight_10 rounded-lg p-4"
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeClick={onEdgeClick}
          onNodeDragStart={onNodeDragStart}
          onNodeDragStop={onNodeDragStop}
          fitView
          fitViewOptions={{padding: 0.1}}
          minZoom={0.5}
          maxZoom={1}
          autoPanOnNodeDrag={false}
          autoPanOnConnect={false}
          panOnDrag={false}
          panOnScroll={false}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          style={{background: 'transparent'}}
          connectionLineStyle={connectionLineStyle}
          connectionMode={ConnectionMode.Loose}
          proOptions={{hideAttribution: true}}
        />
      </div>
    </FlowProvider>
  )
}
